import React from 'react';
import { Popover } from '@mui/material';

import { Button } from 'components/Button';

import type { RiskRatingValue } from '../types';
import { getRiskCellInfo } from '../utils';
import {
  StyledRiskRatingAxisLabel,
  StyledRiskRatingHeaderCell,
  StyledRiskRatingHeaderLabel,
  StyledRiskRatingMatrixCell,
  StyledRiskRatingMatrixGrid,
  StyledRiskRatingMatrixWithAxis,
  StyledRiskRatingPopoverContent,
  StyledRiskRatingPopoverFooter,
  StyledRiskRatingSeverityAxisLabel,
} from '../styles';

interface RiskRatingMatrixPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  currentRating: RiskRatingValue | null;
  likelihoodLabels: string[];
  severityLabels: string[];
  onSelect: (likelihood: number, severity: number) => void;
  onClear: () => void;
  onClose: () => void;
}

export const RiskRatingMatrixPopover: React.FC<RiskRatingMatrixPopoverProps> = ({
  open,
  anchorEl,
  currentRating,
  likelihoodLabels,
  severityLabels,
  onSelect,
  onClear,
  onClose,
}) => {
  const matrixRows = severityLabels.length;
  const matrixColumns = likelihoodLabels.length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // Avoid restoring focus to a possibly-detached anchor after the rating transaction
      // re-renders the table — if restoration falls through to <body>, the browser jumps
      // to the document top. Auto-focus stays on so keyboard users still land in the matrix.
      disableRestoreFocus
    >
      <StyledRiskRatingPopoverContent>
        <StyledRiskRatingMatrixWithAxis>
          <StyledRiskRatingSeverityAxisLabel>Severity →</StyledRiskRatingSeverityAxisLabel>
          <StyledRiskRatingMatrixGrid $cols={matrixColumns}>
          <StyledRiskRatingAxisLabel />
          <StyledRiskRatingAxisLabel />
          {likelihoodLabels.map((label, i) => (
            <StyledRiskRatingHeaderCell key={`l-${i}`}>
              {i + 1}
              <StyledRiskRatingHeaderLabel>{label}</StyledRiskRatingHeaderLabel>
            </StyledRiskRatingHeaderCell>
          ))}
          {[...severityLabels].map((_sevLabel, sevIdx) => {
            const severity = severityLabels.length - sevIdx;
            return (
              <React.Fragment key={`s-${severity}`}>
                <StyledRiskRatingAxisLabel>{severity}</StyledRiskRatingAxisLabel>
                <StyledRiskRatingAxisLabel $wrap>
                  {severityLabels[severity - 1]}
                </StyledRiskRatingAxisLabel>
                {likelihoodLabels.map((_, lIdx) => {
                  const likelihood = lIdx + 1;
                  const { score, bg, color, label } = getRiskCellInfo(severity, likelihood, matrixRows, matrixColumns);
                  const isSelected =
                    currentRating?.likelihood === likelihood &&
                    currentRating?.severity === severity;
                  return (
                    <StyledRiskRatingMatrixCell
                      key={`cell-${severity}-${likelihood}`}
                      type="button"
                      $bg={bg}
                      $color={color}
                      $selected={isSelected}
                      onClick={() => onSelect(likelihood, severity)}
                      title={`${score} — ${label}`}
                    >
                      {score}
                    </StyledRiskRatingMatrixCell>
                  );
                })}
              </React.Fragment>
            );
          })}
          <StyledRiskRatingAxisLabel />
          <StyledRiskRatingAxisLabel />
          <StyledRiskRatingAxisLabel $bottomAxis $cols={matrixColumns}>
            Likelihood →
          </StyledRiskRatingAxisLabel>
          </StyledRiskRatingMatrixGrid>
        </StyledRiskRatingMatrixWithAxis>
        <StyledRiskRatingPopoverFooter>
          <Button variant="text" size="small" onClick={onClear}>
            Clear
          </Button>
        </StyledRiskRatingPopoverFooter>
      </StyledRiskRatingPopoverContent>
    </Popover>
  );
};
